import { useRef, useState } from 'react';
import './App.css';
import Header from './components/layout/Header';
import FileDropZone from './features/file-upload/components/FileDropZone';
import FileList from './features/file-upload/components/FileList';
import AlertGrid from './features/alerts/components/AlertGrid';
import ValidationSection from './features/validation/components/ValidationSection';
import ValidationResults from './features/validation/components/ValidationResults';
import ErrorDisplay from './features/validation/components/ErrorDisplay';
import schemaFileImage from './resources/schema-file.jpg';
import { useFileUpload } from './features/file-upload/hooks/useFileUpload';
import { useAlerts } from './features/alerts/hooks/useAlerts';
import { useValidation } from './features/validation/hooks/useValidation';

const THRESHOLD_MSG = 'found 1 transaction that has amount more than the threshold (50000)';

function App(): JSX.Element {
  const [isSchemaOpen, setIsSchemaOpen] = useState(false);
  const [schemaModalTop, setSchemaModalTop] = useState(120);
  const uploadSectionHeaderRef = useRef<HTMLDivElement | null>(null);
  const fileUpload = useFileUpload();
  const alerts = useAlerts();
  const validation = useValidation();

  const handleValidate = async (): Promise<void> => {
    if (!fileUpload.parsedJson) return;
    await validation.runValidation(
      fileUpload.parsedJson,
      alerts.selectedAlerts,
      alerts.alertOptions,
      alerts.selectedCountries
    );
  };

  const handleOpenSchema = (): void => {
    const headerTop = uploadSectionHeaderRef.current?.getBoundingClientRect().top;
    if (typeof headerTop === 'number') {
      setSchemaModalTop(Math.max(16, Math.round(headerTop)));
    }
    setIsSchemaOpen(true);
  };

  return (
    <div className="App">
      <Header />

      <main className="App-main">
        {/* File Upload Section */}
        <section className="App-section">
          <div className="App-section-header" ref={uploadSectionHeaderRef}>
            <div className="App-section-header-top">
              <h2 className="App-section-title">Upload Payment File</h2>
              <button
                type="button"
                className="App-schema-button"
                onClick={handleOpenSchema}
                aria-haspopup="dialog"
                aria-expanded={isSchemaOpen}
              >
                File schema
              </button>
            </div>
            <p className="App-section-description">Drag and drop your SEPA XML or CSV file or click to browse</p>
          </div>
          <div className="App-upload-container">
            <FileDropZone onFiles={fileUpload.handleFiles} multiple accept={".xml,.csv"} />
          </div>
          <FileList files={fileUpload.files} onRemoveFile={fileUpload.removeFile} />
        </section>

        {/* Financial Alerts Section */}
        <section className="App-section">
          <div className="App-section-header">
            <h2 className="App-section-title">Rule Set</h2>
          </div>
          <AlertGrid
            alertOptions={alerts.alertOptions}
            selectedAlerts={alerts.selectedAlerts}
            linkedOpsAmount={alerts.linkedOpsAmount}
            linkedOpsHours={alerts.linkedOpsHours}
            linkedOpsBase={alerts.LINKED_OPS_BASE}
            customerCountryListBase={alerts.CUSTOMER_COUNTRY_LIST_BASE}
            selectedCountries={alerts.selectedCountries}
            onAlertChange={alerts.handleAlertChange}
            onLinkedOpsAmountChange={alerts.updateLinkedOpsAmount}
            onLinkedOpsHoursChange={alerts.updateLinkedOpsHours}
            onCountriesChange={alerts.handleCountriesChange}
          />
        </section>

        {/* Validation Section */}
        {fileUpload.parsedJson && (
          <ValidationSection
            isValidating={validation.isValidating}
            onValidate={handleValidate}
          />
        )}

        {/* Error Display */}
        {(fileUpload.error || validation.error) && (
          <ErrorDisplay error={fileUpload.error || validation.error || ''} />
        )}

        {/* Results Section */}
        {validation.evaluationResponse && (
          <ValidationResults
            evaluationResponse={validation.evaluationResponse}
            thresholdMsg={THRESHOLD_MSG}
          />
        )}
      </main>

      {isSchemaOpen && (
        <div
          className="App-schema-modal-overlay"
          role="presentation"
          style={{ paddingTop: `${schemaModalTop}px` }}
          onClick={(): void => setIsSchemaOpen(false)}
        >
          <div
            className="App-schema-modal"
            role="dialog"
            aria-modal="true"
            aria-label="File schema preview"
            onClick={(event): void => event.stopPropagation()}
          >
            <button
              type="button"
              className="App-schema-close-button"
              onClick={(): void => setIsSchemaOpen(false)}
              aria-label="Close file schema"
            >
              Close
            </button>
            <img
              src={schemaFileImage}
              alt="File schema example"
              className="App-schema-image"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
